import type {
  Attachment,
  Budget,
  BudgetService,
  Contract,
  Provider,
  Receipt,
  ReceiptPaymentMethod,
  Supplier,
  Transaction,
} from '../types/data';

const RECEIPT_PAYMENT_METHODS: ReceiptPaymentMethod[] = [
  'pix',
  'dinheiro',
  'cartao-credito',
  'cartao-debito',
  'transferencia',
  'boleto',
  'cheque',
  'outro',
];

function asString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value.trim() : fallback;
}

function asNumber(value: unknown, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function asStringArrayItem<T>(value: unknown, transform: (record: Record<string, unknown>) => T | null) {
  return typeof value === 'object' && value !== null ? transform(value as Record<string, unknown>) : null;
}

function normalizeAttachment(value: unknown): Attachment | null {
  return asStringArrayItem(value, (record) => {
    const id = asString(record.id);
    const name = asString(record.name);

    if (!id || !name) {
      return null;
    }

    return {
      id,
      name,
      date: asString(record.date),
      type: asString(record.type),
      url: asString(record.url) || undefined,
      driveFileId: asString(record.driveFileId) || undefined,
      driveFolderId: asString(record.driveFolderId) || undefined,
    };
  });
}

function normalizeBudgetService(value: unknown): BudgetService | null {
  return asStringArrayItem(value, (record) => {
    const description = asString(record.description);
    if (!description) {
      return null;
    }

    const hasNewShape = record.laborCost !== undefined || record.materialCost !== undefined;
    if (hasNewShape) {
      return {
        description,
        laborCost: Math.max(0, asNumber(record.laborCost, 0)),
        materialCost: Math.max(0, asNumber(record.materialCost, 0)),
      };
    }

    // Migração: versões antigas usavam quantity * unitPrice (sem separação de mão de obra)
    const legacyQuantity = Math.max(0, asNumber(record.quantity, 0));
    const legacyUnitPrice = Math.max(0, asNumber(record.unitPrice, 0));
    const legacyTotal = legacyQuantity > 0 && legacyUnitPrice > 0 ? legacyQuantity * legacyUnitPrice : 0;

    return {
      description,
      laborCost: 0,
      materialCost: legacyTotal,
    };
  });
}

export function normalizeContracts(values: unknown): Contract[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedContracts: Array<Contract | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const number = asString(record.number);

        if (!id || !number) {
          return null;
        }

        const scheduledTime = asString(record.scheduledTime) || undefined;
        const showInAgenda =
          typeof record.showInAgenda === 'boolean'
            ? record.showInAgenda
            : Boolean(scheduledTime); // Migração legada: itens antigos com horário ficam visíveis
        const agendaOnly =
          typeof record.agendaOnly === 'boolean' ? record.agendaOnly : false;

        return {
          id,
          number,
          address: asString(record.address),
          propertyType: asString(record.propertyType),
          realEstate: asString(record.realEstate),
          responsible: asString(record.responsible),
          startDate: asString(record.startDate),
          endDate: asString(record.endDate),
          scheduledTime,
          showInAgenda,
          agendaOnly,
          status: asString(record.status, 'A iniciar'),
          contractedValue: Math.max(0, asNumber(record.contractedValue)),
          paidToProviders: Math.max(0, asNumber(record.paidToProviders)),
          spentOnMaterials: Math.max(0, asNumber(record.spentOnMaterials)),
          notes: asString(record.notes) || undefined,
          attachments: Array.isArray(record.attachments)
            ? record.attachments.map(normalizeAttachment).filter((item): item is Attachment => Boolean(item))
          : [],
        };
      }),
    );

  return normalizedContracts.filter((item): item is Contract => item !== null);
}

export function normalizeTransactions(values: unknown): Transaction[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedTransactions: Array<Transaction | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const description = asString(record.description);
        const type = record.type === 'income' ? 'income' : record.type === 'expense' ? 'expense' : null;

        if (!id || !description || !type) {
          return null;
        }

        return {
          id,
          date: asString(record.date),
          description,
          type,
          amount: Math.max(0, asNumber(record.amount)),
          status: asString(record.status),
          category: asString(record.category) || undefined,
          contractId: asString(record.contractId) || undefined,
          entityId: asString(record.entityId) || undefined,
        };
      }),
    );

  return normalizedTransactions.filter((item): item is Transaction => item !== null);
}

export function normalizeProviders(values: unknown): Provider[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedProviders: Array<Provider | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const name = asString(record.name);

        if (!id || !name) {
          return null;
        }

        return {
          id,
          name,
          specialty: asString(record.specialty),
          phone: asString(record.phone),
          status: asString(record.status, 'Ativo'),
          cpfCnpj: asString(record.cpfCnpj) || undefined,
          email: asString(record.email) || undefined,
          address: asString(record.address) || undefined,
          bankDetails: asString(record.bankDetails) || undefined,
        };
      }),
    );

  return normalizedProviders.filter((item): item is Provider => item !== null);
}

export function normalizeSuppliers(values: unknown): Supplier[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedSuppliers: Array<Supplier | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const name = asString(record.name);

        if (!id || !name) {
          return null;
        }

        return {
          id,
          name,
          category: asString(record.category),
          phone: asString(record.phone),
          contact: asString(record.contact),
          cnpj: asString(record.cnpj) || undefined,
          email: asString(record.email) || undefined,
          address: asString(record.address) || undefined,
          paymentTerms: asString(record.paymentTerms) || undefined,
          bankDetails: asString(record.bankDetails) || undefined,
        };
      }),
    );

  return normalizedSuppliers.filter((item): item is Supplier => item !== null);
}

export function normalizeBudgets(values: unknown): Budget[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedBudgets: Array<Budget | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const number = asString(record.number);

        if (!id || !number) {
          return null;
        }

        const scheduledTime = asString(record.scheduledTime) || undefined;
        const showInAgenda =
          typeof record.showInAgenda === 'boolean'
            ? record.showInAgenda
            : Boolean(scheduledTime); // Migração legada: itens antigos com horário ficam visíveis
        const agendaOnly =
          typeof record.agendaOnly === 'boolean' ? record.agendaOnly : false;

        return {
          id,
          number,
          clientName: asString(record.clientName),
          clientDocument: asString(record.clientDocument) || undefined,
          clientPhone: asString(record.clientPhone) || undefined,
          clientEmail: asString(record.clientEmail) || undefined,
          address: asString(record.address),
          workDescription: asString(record.workDescription) || undefined,
          date: asString(record.date),
          scheduledTime,
          showInAgenda,
          agendaOnly,
          validUntil: asString(record.validUntil) || undefined,
          services: Array.isArray(record.services)
            ? record.services.map(normalizeBudgetService).filter((item): item is BudgetService => Boolean(item))
            : [],
          discount: Math.max(0, asNumber(record.discount, 0)) || undefined,
          paymentTerms: asString(record.paymentTerms) || undefined,
          executionDeadline: asString(record.executionDeadline) || undefined,
          warranty: asString(record.warranty) || undefined,
          notes: asString(record.notes) || undefined,
          attachments: Array.isArray(record.attachments)
            ? record.attachments.map(normalizeAttachment).filter((item): item is Attachment => Boolean(item))
            : [],
        };
      }),
    );

  return normalizedBudgets.filter((item): item is Budget => item !== null);
}

export function normalizeReceipts(values: unknown): Receipt[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedReceipts: Array<Receipt | null> = values.map((value) =>
      asStringArrayItem(value, (record) => {
        const id = asString(record.id);
        const number = asString(record.number);

        if (!id || !number) {
          return null;
        }

        const rawMethod = asString(record.paymentMethod);
        const paymentMethod = RECEIPT_PAYMENT_METHODS.find((method) => method === rawMethod);

        return {
          id,
          number,
          amount: Math.max(0, asNumber(record.amount)),
          clientName: asString(record.clientName),
          clientDocument: asString(record.clientDocument) || undefined,
          clientAddress: asString(record.clientAddress) || undefined,
          description: asString(record.description),
          paymentMethod,
          paymentReference: asString(record.paymentReference) || undefined,
          contractId: asString(record.contractId) || undefined,
          issuerLocation: asString(record.issuerLocation) || undefined,
          date: asString(record.date),
          attachments: Array.isArray(record.attachments)
            ? record.attachments.map(normalizeAttachment).filter((item): item is Attachment => Boolean(item))
            : [],
        };
      }),
    );

  return normalizedReceipts.filter((item): item is Receipt => item !== null);
}
